import React, { useEffect, useState } from 'react'
import { injectIntl } from 'react-intl'
import Spinner from '../../util/spinner-shim'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import moment from 'moment'
import { useParams, useNavigate } from 'react-router-dom'

import Highcharts from 'highcharts'
import { Chart as HighchartsReact } from '@highcharts/react'

import statusCalculate from 'altrac-application-status'
import { splitTemplate } from 'altrac-conversions'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import AppStore from '../../stores/AppStore'
import withAppStore from '../../util/withAppStore'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import Headline from '../partials/ConfigurePageComponents/Headline'
import TelemetryButtons from '../partials/ConfigurePageComponents/TelemetryButtons'
import ConfigureButtons from '../partials/ConfigurePageComponents/ConfigureButtons'
import DateRangePicker from '../partials/ConfigurePageComponents/DateRangePicker'
import { useDateRangePicker } from '../partials/ConfigurePageComponents/useDateRangePicker'
import pageStyles from '../../../css/devicePage.module.css'

const Sections = (props) => {
  const {
    dateBegin,
    dateEnd,
    dateType,
    dayPickerIsVisible,
    draftRange,
    device,
    deviceStatus,
    customer,
    statsLookup,
    statsReady,
    pinCode,
    section: {
      chart: chartInterface,
      telemetryTypes,
      settingTypes,
    },
    chartsData,
    editDateRange,
    closeDayPicker,
    handleDayClick,
    index,
    level,
    tempConv,
  } = props

  let physical
  if (device && device.physical) physical = device.physical

  const [chartInst, setChartInst] = useState(false)

  const catchChart = (_chartInst) => {
    setChartInst(_chartInst)
  }

  const divStyle = { float: 'left', width: '100%' }
  if (index > 0) divStyle.marginTop = '20px'
  const toReturn = []

  const {
    height = '225px',
  } = chartInterface

  const dataReady = chartsData && (statsReady === 'loaded' || statsReady === 'loading')

  let seriesData

  if (chartInterface && typeof chartInterface === 'object' && dataReady) {
    const {
      name = '',
      series = [],
      yAxis = [{
        min: 0,
        max: 1,
        title: {
          enabled: false,
        },
        allowDecimals: false,
        endOnTick: false,
        startOnTick: false,
        opposite: true,
        visible: false,
      }, {
        title: {
          enabled: false,
        },
        allowDecimals: false,
      }],
    } = chartInterface

    seriesData = series.reduce((acc, cur) => {
      if (!dataReady || !chartsData[name] || !physical) return acc
      let {
        label,
      } = cur
      label = splitTemplate(label, physical, true)
      if (chartsData[name][label]) {
        const {
          type,
          color,
          shadow = false,
          yAxis: yAxisSer = 0,
          pointWidth,
          lineWidth = 2,
          fillOpacity = 1,
        } = cur
        acc.push({
          name: label,
          data: chartsData[name][label],
          type,
          color,
          fillOpacity,
          shadow,
          lineWidth,
          yAxis: yAxisSer,
          pointWidth,
        })
      }
      return acc
    }, [])
    if (chartInst) {
      if (!dataReady) {
        chartInst.showLoading()
      } else {
        chartInst.hideLoading()
      }
    }

    const buttonStyle = {
      cursor: 'pointer',
      '&:focus': { outline: '#CCC' },
      '&:hover': { background: '#CCC' },
      paddingRight: '15px',
    }
    toReturn.push(
      <div style={{ float: 'left' }} key={`${index}_chart`}>
        <div className={pageStyles.chartContainer} style={{ padding: '10px 0px', height: 'inherit' }}>
          <div className={pageStyles.dateRangePickerInside} style={{ display: 'flex' }}>
            <DateRangePicker
              dateBegin={dateBegin}
              dateEnd={dateEnd}
              dateType={dateType}
              dayPickerIsVisible={dayPickerIsVisible}
              draftRange={draftRange}
              onClose={closeDayPicker}
              onEditRange={editDateRange}
              onDayClick={handleDayClick}
              layout='inline'
              buttonStyle={{ flex: 1, marginLeft: '35px' }}
            />
          </div>
          <HighchartsReact
            highcharts={Highcharts}
            callback={catchChart}
            options={{
              accessibility: {
                enabled: false,
              },
              title: { text: '' },
              chart: {
                height,
                zoomType: 'x',
                pinchType: 'x',
                panning: {
                  enabled: true,
                },
                animation: false,
                resetZoomButton: {
                  theme: {
                    display: 'none',
                  },
                },
              },
              tooltip: {
                shared: true,
              },
              xAxis: {
                type: 'datetime',
                dateTimeLabelFormats: {
                  day: '%e %b',
                  month: '%b %e',
                  year: '%b',
                },
                title: {
                  enabled: false,
                },
                // tickInterval: 1000 * 60 * 60 * 24,
                tickPixelInterval: 75,
              },
              yAxis,
              plotOptions: {
                series: {
                  states: {
                    inactive: {
                      enabled: false,
                    },
                  },
                },
              },
              legend: {
                enabled: true,
              },
              series: seriesData,
              credits: {
                enabled: false,
              },
              exporting: {
                enabled: false,
              },
            }}
          />
        </div>
      </div>
    )
  } else {
    toReturn.push(
      <div
        className={pageStyles.chartContainer}
        style={{
          padding: '10px 0px',
          height: `calc(${height} + 57px)`,
          float: 'left',
          position: 'relative',
        }}
        key={`${index}_nochart`}
      >
        <div style={{
          height: '100%',
          width: '100%',
          float: 'left',
          position: 'relative',
          backgroundColor: 'white',
        }}
        >
          <Spinner
            name='wave'
            color='#284047'
            style={{
              position: 'absolute',
              top: '50%',
              right: '50%',
              transform: 'translate(50%, -50%)',
            }}
          />
        </div>
      </div>
    )
  }

  if (telemetryTypes && Array.isArray(telemetryTypes) && telemetryTypes.length > 0) {
    toReturn.push(
      <div style={{ float: 'left' }} key={`${index}_tel`}>
        <TelemetryButtons
          device={device}
          status={deviceStatus}
          path={`/${device.customer_id}/devices/${device.id}/`}
          telemetryTypes={telemetryTypes}
          statsLookup={statsLookup}
          statsReady={statsReady}
          customer={customer}
          pinCode={pinCode}
          level={level}
          tempConv={tempConv}
        />
      </div>
    )
  }

  if (settingTypes && Array.isArray(settingTypes) && settingTypes.length > 0) {
    const {
      application_settings: applicationSettings,
      application_settings_new: applicationSettingsNew,
      reading0,
      interface_versioned: interfaceVersioned,
    } = device
    toReturn.push(
      <div style={{ float: 'left' }} key={`${index}_set`}>
        <ConfigureButtons
          applicationSettings={applicationSettings}
          applicationSettingsNew={applicationSettingsNew}
          reading0={reading0}
          physical={physical}
          interfaceVersioned={interfaceVersioned}
          customer={customer}
          pinCode={pinCode}
          path={`/${device.customer_id}/devices/${device.id}/settings/`}
          settingTypes={settingTypes}
        />
      </div>
    )
  }
  return toReturn
}

function ValvePage (props) {
  const { customerID, deviceID } = useParams()
  const navigate = useNavigate()

  const viewDays = 10

  // Use the shared date range picker hook
  const dateRangePicker = useDateRangePicker(
    moment().startOf('day').subtract(viewDays, 'days'),
    moment().endOf('day')
  )

  const [state, setState] = React.useState({
    viewDays,
    statsReady: 'notLoaded',
  })

  // Sync date range picker state with local state when needed
  const { dateBegin, dateEnd, dateType, dayPickerIsVisible, draftRange } = dateRangePicker.state

  const resizeTimeout = React.useRef(false)
  const isInitialMount = React.useRef(true)
  const prevViewDays = React.useRef(state.viewDays)

  const getDevice = React.useCallback(() => {
    AppDispatcher.dispatch({ action: 'get-device', deviceID })
  }, [deviceID])

  const getStats = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-valve-stats',
      deviceID,
      timeBeg: dateBegin.toISOString(),
      timeEnd: dateEnd.toISOString(),
      utcOffset: moment().utcOffset() / 60,
    })
  }, [deviceID, dateBegin, dateEnd])

  const back = React.useCallback(() => {
    const { data: { history: { list = [] } = {} } = {} } = props
    navigate(list.length > 0 ? list : '/home')
  }, [props.data.history.list, navigate])

  const goto = React.useCallback((path) => {
    navigate(path)
  }, [navigate])

  const toAdvancedSettings = React.useCallback(() => {
    goto(`/${customerID}/devices/${deviceID}/settings`)
  }, [customerID, deviceID, goto])

  // Use handlers from the date range picker hook
  const { closeDayPicker, editDateRange, handleDayClick, applyDraftRange } = dateRangePicker

  const resetDateRange = React.useCallback(() => {
    const dateBegin = moment()
      .startOf('day')
      .subtract(state.viewDays, 'days')

    const dateEnd = moment()
      .endOf('day')

    dateRangePicker.setDateRange(dateBegin, dateEnd)
  }, [state.viewDays, dateRangePicker])

  // Use resetToLast31Days from the hook
  const { resetToLast31Days } = dateRangePicker

  const onChange = React.useCallback(() => {
    const { statsReady } = state
    const {
      data: {
        statsReady: statsReadyN,
      },
    } = AppStore
    if (statsReady !== statsReadyN) {
      setState(prev => ({ ...prev, statsReady: statsReadyN }))
    }
  }, [state.statsReady])

  const handleResize = React.useCallback(() => {
    clearTimeout(resizeTimeout.current)
    resizeTimeout.current = setTimeout(() => {
      // Force re-render
      setState(prev => ({ ...prev }))
    }, 500)
  }, [])

  useEffect(() => {
    getStats()
    isInitialMount.current = false
  }, [])

  React.useEffect(() => {
    getDevice()
    AppStore.addChangeListener(onChange)
    window.addEventListener('resize', handleResize)
    const interval = setInterval(getDevice, 5000)
    const interval2 = setInterval(getStats, 60000)

    return () => {
      AppStore.removeListener('change', onChange)
      window.removeEventListener('resize', handleResize)
      clearInterval(interval)
      clearInterval(interval2)
    }
  }, [getDevice, onChange, handleResize, getStats])

  // Call getStats when dateBegin or dateEnd changes (skip initial mount)
  React.useEffect(() => {
    if (!isInitialMount.current) {
      getStats()
    }
  }, [dateBegin, dateEnd, getStats])

  // Call resetDateRange when viewDays changes
  React.useEffect(() => {
    if (prevViewDays.current !== state.viewDays && !isInitialMount.current) {
      resetDateRange()
    }
    prevViewDays.current = state.viewDays
  }, [state.viewDays, resetDateRange])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}
    const device = lookup[deviceID]

    if (lookupReady && device) {
      const {
        physical: {
          pumpSettings: {
            viewDays: newViewDays = 10,
          } = {},
        } = {},
      } = device || {}

      if (state.viewDays !== newViewDays) {
        setState(prev => ({ ...prev, viewDays: newViewDays }))
      }
    }
  }, [props.data.lookupReady, props.data.lookup, deviceID, state.viewDays, resetDateRange])

  const render = () => {
    const {
      data: {
        lookupReady,
        lookup,
        stats: { charts: chartsData } = {},
        statsLookup,
        pinCode,
        customer: {
          data: customer,
        },
      } = {},
    } = AppStore

    const {
      statsReady,
    } = state

    const {
      dateBegin,
      dateEnd,
      dateType,
      dayPickerIsVisible,
    } = dateRangePicker.state

    const {
      auth: { logout },
      intl: { formatMessage },
    } = props

    let pageContents = [(
      <div key='spinner' style={{ height: '400px', position: 'relative' }}>
        <Spinner
          name='wave'
          style={{
            position: 'absolute',
            top: '50%',
            right: '50%',
            transform: 'translate(50%, -50%)',
          }}
        />
      </div>
    )]

    let name
    let device
    let headline = ''

    let deviceStatus
    if (lookupReady && lookup) {
      device = lookup[deviceID]
      deviceStatus = statusCalculate(device)

      const {
        physical: {
          deviceNumber,
        } = {},
      } = device || {}

      name = deviceNumber

      const {
        deviceHeadlineDanger,
        deviceHeadlineWarning,
      } = deviceStatus

      headline = (
        <div>
          <Headline
            info={deviceHeadlineDanger}
            prepend={formatMessage({
              id: 'headline.error',
              defaultMessage: 'ERROR: ',
              description: '',
            })}
          />
          <Headline
            info={deviceHeadlineWarning}
            prepend={formatMessage({
              id: 'headline.warning',
              defaultMessage: 'WARNING: ',
              description: '',
            })}
          />
        </div>
      )
    }

    let deviceName = ''
    if (lookupReady && lookup) {
      const {
        interface_versioned: {
          data: {
            primaryPage: {
              sections = [],
            } = {},
          } = {},
        } = {},
        address_alias: alias,
        physical: {
          deviceNumber,
        } = {},
      } = device || {}
      deviceName = deviceNumber || alias || ''

      if (
        sections &&
        Array.isArray(sections)
      ) {
          pageContents = []
          for (let i = 0; i < sections.length; i++) {
            pageContents.push(
              <Sections
                key={i}
                device={device}
              deviceStatus={deviceStatus}
              customer={customer}
              statsLookup={statsLookup}
              statsReady={statsReady}
              pinCode={pinCode}
              dateBegin={dateBegin}
              dateEnd={dateEnd}
              dateType={dateType}
              dayPickerIsVisible={dayPickerIsVisible}
              draftRange={draftRange}
              section={sections[i]}
              chartsData={chartsData}
              editDateRange={editDateRange}
              closeDayPicker={closeDayPicker}
              handleDayClick={handleDayClick}
              index={i}
              level={props.level}
              tempConv={(props.data.user && props.data.user.locale.tempConv) || 'f'}
            />
          )
        }
      }
    }

    return (
      <div>
        <Helmet><title>{deviceName || 'Valve'}</title></Helmet>
        <DateRangePicker
          dateBegin={dateBegin}
          dateEnd={dateEnd}
          dateType={dateType}
          dayPickerIsVisible={dayPickerIsVisible}
          draftRange={draftRange}
          onClose={closeDayPicker}
          onEditRange={editDateRange}
          onDayClick={handleDayClick}
          onApplyRange={applyDraftRange}
          onResetToLast31Days={resetToLast31Days}
          layout='modal'
        />
        <NavigationBar
          backFunc={back}
          page='/valve'
          name={deviceName || name || 'Valve'}
          logOut={() => logout(navigate)}
          rightControl={{
            icon: 'fa-cog',
            func: toAdvancedSettings,
          }}
        />
        <div className={pageStyles.pageWidth}>
          <div className={pageStyles.pageContainer}>
            {statsReady === 'forbidden' && (
              <div className={pageStyles.viewerAccessBanner}>
                {formatMessage({
                  id: 'valve.data.viewerAccess',
                  defaultMessage: 'Valve data is not available for viewers. Contact an administrator for access.',
                  description: 'Banner shown when viewer users cannot access valve data',
                })}
              </div>
            )}
            {headline}
            {pageContents}
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return render()
}

ValvePage.propTypes = {
  auth: PropTypes.object,
  data: PropTypes.object,
  viewDays: PropTypes.number,
}

export default withAppStore(injectIntl(ValvePage))
